/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Count } from '../models/count';
import { Flight } from '../models/flight';
import { FlightPartial } from '../models/flight-partial';
import { FlightWithRelations } from '../models/flight-with-relations';
import { NewFlight } from '../models/new-flight';
import { Tour } from '../models/tour';

@Injectable({
  providedIn: 'root',
})
export class FlightControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation flightControllerCreateAll
   */
  static readonly FlightControllerCreateAllPath = '/flights/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll$Response(params?: {
    body?: Array<NewFlight>
  }): Observable<StrictHttpResponse<Array<Flight>>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Flight>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll(params?: {
    body?: Array<NewFlight>
  }): Observable<Array<Flight>> {

    return this.createAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Flight>>) => r.body as Array<Flight>)
    );
  }

  /**
   * Path part for operation flightControllerCount
   */
  static readonly FlightControllerCountPath = '/flights/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<Count> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation flightControllerFindTour
   */
  static readonly FlightControllerFindTourPath = '/flights/{id}/tour';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findTour()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTour$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<Tour>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerFindTourPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Tour>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findTour$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTour(params: {
    id: string;
  }): Observable<Tour> {

    return this.findTour$Response(params).pipe(
      map((r: StrictHttpResponse<Tour>) => r.body as Tour)
    );
  }

  /**
   * Path part for operation flightControllerFindById
   */
  static readonly FlightControllerFindByIdPath = '/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<FlightWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FlightWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<FlightWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<FlightWithRelations>) => r.body as FlightWithRelations)
    );
  }

  /**
   * Path part for operation flightControllerReplaceById
   */
  static readonly FlightControllerReplaceByIdPath = '/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById$Response(params: {
    id: string;
    body?: Flight
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById(params: {
    id: string;
    body?: Flight
  }): Observable<void> {

    return this.replaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation flightControllerDeleteById
   */
  static readonly FlightControllerDeleteByIdPath = '/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation flightControllerUpdateById
   */
  static readonly FlightControllerUpdateByIdPath = '/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: FlightPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: FlightPartial
  }): Observable<void> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation flightControllerFind
   */
  static readonly FlightControllerFindPath = '/flights';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<FlightWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<FlightWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<FlightWithRelations>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<FlightWithRelations>>) => r.body as Array<FlightWithRelations>)
    );
  }

  /**
   * Path part for operation flightControllerCreate
   */
  static readonly FlightControllerCreatePath = '/flights';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewFlight
  }): Observable<StrictHttpResponse<Flight>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Flight>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewFlight
  }): Observable<Flight> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<Flight>) => r.body as Flight)
    );
  }

  /**
   * Path part for operation flightControllerUpdateAll
   */
  static readonly FlightControllerUpdateAllPath = '/flights';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAll$Response(params?: {
    where?: any;
    body?: FlightPartial
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerUpdateAllPath, 'patch');
    if (params) {
      rb.query('where', params.where, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAll(params?: {
    where?: any;
    body?: FlightPartial
  }): Observable<Count> {

    return this.updateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation flightControllerProxyCreateAll
   */
  static readonly FlightControllerProxyCreateAllPath = '/proxy/flights/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll$Response(params?: {
    body?: Array<NewFlight>
  }): Observable<StrictHttpResponse<Array<Flight>>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Flight>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll(params?: {
    body?: Array<NewFlight>
  }): Observable<Array<Flight>> {

    return this.proxyCreateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Flight>>) => r.body as Array<Flight>)
    );
  }

  /**
   * Path part for operation flightControllerProxyCount
   */
  static readonly FlightControllerProxyCountPath = '/proxy/flights/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount(params?: {
    where?: any;
  }): Observable<Count> {

    return this.proxyCount$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation flightControllerProxyFindTour
   */
  static readonly FlightControllerProxyFindTourPath = '/proxy/flights/{id}/tour';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindTour()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindTour$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<Tour>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyFindTourPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Tour>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindTour$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindTour(params: {
    id: string;
  }): Observable<Tour> {

    return this.proxyFindTour$Response(params).pipe(
      map((r: StrictHttpResponse<Tour>) => r.body as Tour)
    );
  }

  /**
   * Path part for operation flightControllerProxyFindById
   */
  static readonly FlightControllerProxyFindByIdPath = '/proxy/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<FlightWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FlightWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById(params: {
    id: string;
    filter?: any;
  }): Observable<FlightWithRelations> {

    return this.proxyFindById$Response(params).pipe(
      map((r: StrictHttpResponse<FlightWithRelations>) => r.body as FlightWithRelations)
    );
  }

  /**
   * Path part for operation flightControllerProxyReplaceById
   */
  static readonly FlightControllerProxyReplaceByIdPath = '/proxy/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyReplaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById$Response(params: {
    id: string;
    body?: Flight
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyReplaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById(params: {
    id: string;
    body?: Flight
  }): Observable<void> {

    return this.proxyReplaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation flightControllerProxyDeleteById
   */
  static readonly FlightControllerProxyDeleteByIdPath = '/proxy/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyDeleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyDeleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById(params: {
    id: string;
  }): Observable<void> {

    return this.proxyDeleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation flightControllerProxyUpdateById
   */
  static readonly FlightControllerProxyUpdateByIdPath = '/proxy/flights/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById$Response(params: {
    id: string;
    body?: FlightPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById(params: {
    id: string;
    body?: FlightPartial
  }): Observable<void> {

    return this.proxyUpdateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation flightControllerProxyFind
   */
  static readonly FlightControllerProxyFindPath = '/proxy/flights';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFind()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<FlightWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<FlightWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFind$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind(params?: {
    filter?: any;
  }): Observable<Array<FlightWithRelations>> {

    return this.proxyFind$Response(params).pipe(
      map((r: StrictHttpResponse<Array<FlightWithRelations>>) => r.body as Array<FlightWithRelations>)
    );
  }

  /**
   * Path part for operation flightControllerProxyCreate
   */
  static readonly FlightControllerProxyCreatePath = '/proxy/flights';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate$Response(params?: {
    body?: NewFlight
  }): Observable<StrictHttpResponse<Flight>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Flight>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate(params?: {
    body?: NewFlight
  }): Observable<Flight> {

    return this.proxyCreate$Response(params).pipe(
      map((r: StrictHttpResponse<Flight>) => r.body as Flight)
    );
  }

  /**
   * Path part for operation flightControllerProxyUpdateAll
   */
  static readonly FlightControllerProxyUpdateAllPath = '/proxy/flights';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateAll$Response(params?: {
    where?: any;
    body?: FlightPartial
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, FlightControllerService.FlightControllerProxyUpdateAllPath, 'patch');
    if (params) {
      rb.query('where', params.where, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateAll(params?: {
    where?: any;
    body?: FlightPartial
  }): Observable<Count> {

    return this.proxyUpdateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

}
